import React, { ReactNode } from "react";
import {
  Autocomplete,
  Chip,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";
import theme from "../../theme";

interface Props {
  control: Control<any, any>;
  fieldName: string;
  options: any[];
  required?: boolean;
  label: string;
  disabled?: boolean;
  noOptionsText?: string;
  multiple?: boolean;
  renderOption?: (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any
  ) => ReactNode;
  onChange?: (value: any) => void;
  defaultValue?: any[] | any;
  onDisableOption?: (value: any) => boolean;
}

const FormAutocomplete: React.FC<Props> = (props) => {
  const {
    control,
    fieldName,
    options,
    required,
    label,
    disabled,
    noOptionsText,
    multiple,
    renderOption,
    onChange,
    defaultValue,
    onDisableOption,
  } = props;

  const handleOnChange = (value: any) => {
    if (onChange) {
      onChange(value);
    }
  };

  const handleDisableOption = (value: any) => {
    if (onDisableOption) {
      onDisableOption(value);
    }
  };

  const defaultRenderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any
  ) => {
    return (
      <li
        {...props}
        key={option?.id}
        style={{ backgroundColor: `${theme.palette.background.paper}` }}
      >
        {option?.value || option?.name || option?.type || ""}
      </li>
    );
  };

  const customPopper = function (props: any) {
    return <Popper {...props} placement="bottom" />;
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{
        required: {
          value: required!,
          message: "Required",
        },
      }}
      render={({ field, fieldState }) => {
        return (
          <Autocomplete
            {...field}
            key={JSON.stringify(options)}
            multiple={!!multiple}
            disablePortal
            options={options}
            filterOptions={(x) => x}
            getOptionDisabled={onDisableOption}
            getOptionLabel={(option) => {
              return (
                option?.name ||
                option?.value ||
                option?.type ||
                option?.firstName + " " + option?.lastName ||
                ""
              );
            }}
            noOptionsText={
              <Typography color="primary">
                {noOptionsText ? noOptionsText : "No Results"}
              </Typography>
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            PopperComponent={customPopper}
            readOnly={disabled}
            disabled={disabled}
            renderOption={renderOption || defaultRenderOption}
            defaultValue={defaultValue || multiple ? [] : null}
            fullWidth
            renderTags={
              multiple
                ? (value: any[], getItemProps) =>
                    value.map((option, index: number) => {
                      const { key: itemKey, ...itemProps } = getItemProps({
                        index,
                      });
                      return (
                        <Tooltip
                          key={itemKey}
                          title={option.value || option.name}
                        >
                          <Chip
                            variant="outlined"
                            label={option.value || option.name}
                            {...itemProps}
                          />
                        </Tooltip>
                      );
                    })
                : undefined
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={label}
                helperText={fieldState.error ? fieldState.error.message : null}
                error={!!fieldState.error}
                variant="outlined"
                type={"text"}
              />
            )}
            value={
              defaultValue ??
              (!!multiple && !!field.value
                ? options.filter((x) =>
                    field?.value?.map((v: any) => v.id)?.includes(x.id)
                  )
                : !multiple && !!field.value
                ? options.find((x) => x.id === field.value.id)
                : !!multiple && !field?.value
                ? []
                : null)
            }
            onChange={(_, value) => {
              field.onChange(value);
              handleOnChange(value);
            }}
          />
        );
      }}
    />
  );
};

export default FormAutocomplete;
